import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function exportDiscountSalesReportExcel(report, fileName) {
  if (!report) return;

  const meta = report.meta || {};
  const name =
    fileName ||
    `discount-sales-report-${meta.branchId}-${meta.year}-${String(meta.monthIndex + 1).padStart(2, "0")}.xlsx`;

  const summaryRows = [
    { Metric: "Month", Value: meta.monthLabel },
    { Metric: "Branch", Value: meta.branchLabel },
    { Metric: "Employee filter", Value: meta.employeeName === "all" ? "All" : meta.employeeName },
    { Metric: "Calculation", Value: "Item-wise (per line)" },
    { Metric: "Total Outward Supply Sales", Value: report.cards?.totalOutwardSales?.amount ?? 0 },
    { Metric: "Non-Discount Sales (an Employee only)", Value: report.cards?.nonDiscountSales?.amount ?? 0 },
    { Metric: "Commission Sales", Value: report.cards?.employeeSales?.amount ?? 0 },
    { Metric: "Discounted Sales", Value: report.cards?.discountedSales?.amount ?? 0 },
    { Metric: "Total Discount Given", Value: report.cards?.discountedSales?.discountAmount ?? 0 },
    { Metric: "Discount % of Sales", Value: report.cards?.discountPercentage ?? 0 },
    { Metric: "Item lines", Value: meta.itemCount ?? report.cards?.totalOutwardSales?.lineCount ?? 0 },
    { Metric: "Invoices", Value: meta.invoiceCount ?? report.cards?.totalOutwardSales?.invoiceCount ?? 0 },
  ];

  const rankingRows = (report.employeeRanking || []).map((r) => ({
    Rank: r.rank,
    Employee: r.employee,
    "Sales Amount": r.salesAmount,
    "Item Count": r.lineCount ?? r.invoiceCount,
    "Avg Line": r.averageLine ?? r.averageBill,
    "Contribution %": r.contributionPct,
  }));

  const pieRows = (report.charts?.pie || []).map((r) => ({
    Segment: r.name,
    Amount: r.value,
    "Item Count": r.lineCount ?? r.invoiceCount ?? "",
  }));

  const wb = XLSX.utils.book_new();
  const addSheet = (title, rows) => {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows.length ? rows : [{ Empty: true }]),
      title
    );
  };

  addSheet("Summary", summaryRows);
  addSheet("Pie Chart", pieRows);

  const breakdownRows = (report.charts?.discountBreakdown || []).map((r) => ({
    "Discount % Range": r.label,
    Items: r.lineCount ?? r.invoiceCount,
    Invoices: r.invoiceCount ?? "",
    "Net Sales": r.netSales,
    "Discount Amount": r.discountAmount,
  }));
  if (breakdownRows.length) addSheet("Discount Breakdown", breakdownRows);

  const lineRows = (report.discountedLineItems || report.discountedInvoices || []).map((line) => ({
    "Invoice No": line.invoiceNo,
    Date: line.invoiceDate,
    Item: line.itemName,
    "Part No": line.partNo,
    Qty: line.qty,
    Customer: line.customerName,
    Employee: line.employee,
    Gross: line.grossAmount,
    Discount: line.discountAmount,
    Net: line.netAmount,
    "Disc %": line.discountPct,
    "Disc % Range": line.discountPctBucket,
  }));
  if (lineRows.length) addSheet("Discounted Lines", lineRows);

  addSheet("Commission Ranking", rankingRows);

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), name);
}

export function exportDiscountSalesReportPdf(report, fileName) {
  if (!report) return;

  const meta = report.meta || {};
  const name =
    fileName ||
    `discount-sales-report-${meta.branchId}-${meta.year}-${String(meta.monthIndex + 1).padStart(2, "0")}.pdf`;

  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14);
  doc.text("Discount vs Non-Discount Sales Report (Item-wise)", 14, 16);
  doc.setFontSize(9);
  doc.text(
    `${meta.monthLabel} · ${meta.branchLabel} · ${meta.employeeName === "all" ? "All employees" : meta.employeeName}`,
    14,
    23
  );

  autoTable(doc, {
    startY: 28,
    head: [["Metric", "Value"]],
    body: [
      ["Total Outward Supply", fm(report.cards?.totalOutwardSales?.amount)],
      ["Non-Discount Sales", fm(report.cards?.nonDiscountSales?.amount)],
      ["Commission Sales", fm(report.cards?.employeeSales?.amount)],
      ["Discounted Sales", fm(report.cards?.discountedSales?.amount)],
      ["Total Discount Given", fm(report.cards?.discountedSales?.discountAmount)],
      ["Discount % of Sales", `${(report.cards?.discountPercentage ?? 0).toFixed(1)}%`],
      ["Item lines", String(meta.itemCount ?? report.cards?.totalOutwardSales?.lineCount ?? 0)],
    ],
    styles: { fontSize: 8 },
    headStyles: { fillColor: [93, 0, 129] },
  });

  const rankingBody = (report.employeeRanking || []).map((r) => [
    r.rank,
    r.employee,
    fm(r.salesAmount),
    r.lineCount ?? r.invoiceCount,
    fm(r.averageLine ?? r.averageBill),
    `${r.contributionPct.toFixed(1)}%`,
  ]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Rank", "Employee", "Sales", "Items", "Avg Line", "Contribution %"]],
    body: rankingBody.length ? rankingBody : [["—", "—", "—", "—", "—", "—"]],
    styles: { fontSize: 7 },
    headStyles: { fillColor: [93, 0, 129] },
  });

  doc.save(name);
}
